import {
  InfiniteData,
  QueryClient,
  UseInfiniteQueryResult,
  UseQueryResult,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { PaginatedResponse } from '@/types/api';
import { normalizePresentIds } from '@/utils/ids';

export const CATALOGUE_SEARCH_DEBOUNCE_MS = 500;
export const DEFAULT_CATALOGUE_IMAGE = '/default-logo.svg';

export type CatalogueEntry = {
  id: string;
  name: string;
  parameters?: { main_logo_presigned_url?: string } | null;
};

export type CatalogueOption = { value: string; label: string; image: string };

export type CatalogueOptionRecord<TEntry> = {
  option: CatalogueOption;
  /** Absent for the synthetic option built from `fallbackLabel`. */
  entity?: TEntry;
};

export type CataloguePickerValue =
  | string
  | readonly (string | null | undefined)[]
  | null
  | undefined;

export type CataloguePickerParams = {
  /** One id or several; the ids the picker must keep named whatever is loaded. */
  value: CataloguePickerValue;
  /** The label stored on the presence or declaration, shown when the value cannot be resolved. */
  fallbackLabel?: string | null;
  /** For components mounted only while their dropdown is open, which have no open event. */
  openOnMount?: boolean;
  enabled?: boolean;
  staleTime?: number;
};

export type CataloguePicker<TEntry> = {
  options: CatalogueOption[];
  onSearch: (term: string) => void;
  onOpen: () => void;
  onEndReached: () => void;
  isLoadingMore: boolean;
  hasNextPage: boolean;
  isLoadingFirstPage: boolean;
  /** The pages, not the value: a refetch or a failure of the listed catalogue. */
  isFetching: boolean;
  isError: boolean;
  isValueResolving: boolean;
  findOption: (id: string | null | undefined) => CatalogueOptionRecord<TEntry> | undefined;
};

type UseCataloguePickerArgs<TEntry, TParams> = CataloguePickerParams & {
  params: TParams;
  /** The catalogue's root query key, to find an entry another picker of it has loaded. */
  cacheKey: readonly unknown[];
  useInfinite: (
    params: TParams & { search?: string },
    config: { staleTime?: number; enabled?: boolean }
  ) => UseInfiniteQueryResult<InfiniteData<PaginatedResponse<TEntry>>>;
  useSelected: (
    ids: string[],
    options: { initialData?: PaginatedResponse<TEntry> }
  ) => UseQueryResult<PaginatedResponse<TEntry>>;
};

/**
 * RISK: an option keeps the image URL of the first answer that listed it, and that URL is
 * presigned for an hour. Anything stored with the entry takes the entity's latest one instead.
 */
export const latestImageOf = <TEntry extends CatalogueEntry>(
  record: CatalogueOptionRecord<TEntry> | undefined
) => record?.entity?.parameters?.main_logo_presigned_url || record?.option.image;

const NO_IDS: string[] = [];

const toIds = (value: CataloguePickerValue) =>
  normalizePresentIds(typeof value === 'string' ? [value] : (value ?? []));

type CachedAnswer<TEntry> = { pages?: { data?: TEntry[] }[]; data?: TEntry[] | TEntry } | undefined;

/** An entry from any cached answer of the catalogue: pages, id sets or one-page lists. */
export function findCachedEntry<TEntry extends CatalogueEntry>(
  queryClient: QueryClient,
  cacheKey: readonly unknown[],
  id: string
) {
  for (const [, answer] of queryClient.getQueriesData<CachedAnswer<TEntry>>({
    queryKey: cacheKey,
  })) {
    const entries = answer?.pages?.flatMap(page => page.data ?? []) ?? answer?.data;
    const found = Array.isArray(entries) ? entries.find(entry => entry?.id === id) : undefined;
    if (found) return found;
  }
  return undefined;
}

/**
 * The data behind a logo or person picker: the catalogue paged and searched on the server, plus
 * the value resolved by id and appended when it is not on a loaded page.
 */
export function useCataloguePicker<TEntry extends CatalogueEntry, TParams extends object>({
  value,
  fallbackLabel,
  openOnMount = false,
  enabled = true,
  staleTime,
  params,
  cacheKey,
  useInfinite,
  useSelected,
}: UseCataloguePickerArgs<TEntry, TParams>): CataloguePicker<TEntry> {
  const queryClient = useQueryClient();
  const [hasOpened, setHasOpened] = useState(openOnMount);
  const [search, setSearch] = useState('');
  const searchTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(searchTimer.current), []);

  const onSearch = useCallback((term: string) => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setSearch(term), CATALOGUE_SEARCH_DEBOUNCE_MS);
  }, []);

  // `Select` empties its own search box when it opens without reporting it, and this hook
  // outlives the dropdown, so the term is dropped here or the list would stay narrowed by it.
  const onOpen = useCallback(() => {
    clearTimeout(searchTimer.current);
    setSearch('');
    setHasOpened(true);
  }, []);

  const isListing = enabled && hasOpened;
  const pages = useInfinite(
    { ...params, search: search || undefined },
    { staleTime, enabled: isListing }
  );

  const valueKey = toIds(value).join(',');
  const ids = useMemo(() => (valueKey ? valueKey.split(',') : []), [valueKey]);

  // A value chosen from a loaded page is already known, so resolving it by id would only cost a
  // request. It seeds the resolution instead, which other views of the same ids then share.
  const pagedEntries = pages.data?.pages;
  // RISK: the seed and the query share these ids. Seeding with ids the query does not ask for
  // would store entities under the key of another id set.
  const queryIds = enabled ? ids : NO_IDS;
  const knownAnswer = useMemo(() => {
    if (queryIds.length === 0 || !pagedEntries) return undefined;
    const pagedById = new Map(
      pagedEntries.flatMap(page => page.data).map(entry => [entry.id, entry])
    );
    const known = queryIds.map(id => pagedById.get(id));
    if (!known.every(Boolean)) return undefined;
    return { data: known as TEntry[], total: known.length } as PaginatedResponse<TEntry>;
  }, [queryIds, pagedEntries]);
  const resolved = useSelected(queryIds, { initialData: knownAnswer });

  // One object per id across pages and answers, so `Select` does not re-match every option
  // on each page and callers can hold on to an option. Every answer carries a freshly presigned
  // image URL, so only a renamed entry gets a new object.
  const cache = useRef(new Map<string, CatalogueOptionRecord<TEntry>>());
  const recordOf = useCallback((entity: TEntry) => {
    const cached = cache.current.get(entity.id);
    if (cached?.entity && cached.option.label === entity.name) {
      cached.entity = entity;
      return cached;
    }
    const record = {
      option: {
        value: entity.id,
        label: entity.name,
        image: entity.parameters?.main_logo_presigned_url || '',
      },
      entity,
    };
    cache.current.set(entity.id, record);
    return record;
  }, []);

  const resolvedEntries = resolved.data?.data;
  const isResolutionSettled = resolved.isSuccess || resolved.isError;

  const { options, records } = useMemo(() => {
    const byId = new Map<string, CatalogueOptionRecord<TEntry>>();
    const listed: CatalogueOption[] = [];

    (pagedEntries ?? []).forEach(page =>
      page.data.forEach(entity => {
        if (byId.has(entity.id)) return;
        const record = recordOf(entity);
        byId.set(entity.id, record);
        listed.push(record.option);
      })
    );

    // Appended rather than merged in place: the pages keep the backend's alphabetical order,
    // and a value they do not carry goes at the end instead of disappearing.
    (resolvedEntries ?? []).forEach(entity => {
      if (!ids.includes(entity.id) || byId.has(entity.id)) return;
      const record = recordOf(entity);
      byId.set(entity.id, record);
      listed.push(record.option);
    });

    if (isResolutionSettled && fallbackLabel) {
      ids
        .filter(id => !byId.has(id))
        .forEach(id => {
          const record = {
            option: { value: id, label: fallbackLabel, image: DEFAULT_CATALOGUE_IMAGE },
          };
          byId.set(id, record);
          listed.push(record.option);
        });
    }

    return { options: listed, records: byId };
  }, [pagedEntries, resolvedEntries, ids, isResolutionSettled, fallbackLabel, recordOf]);

  const isValueResolving = enabled && ids.some(id => !records.has(id)) && !isResolutionSettled;

  // An id chosen in another picker of the same catalogue, e.g. the inline dropdown of a batch
  // move, is on a page that picker loaded rather than on this one's.
  const findOption = useCallback(
    (id: string | null | undefined) => {
      if (!id) return undefined;
      const record = records.get(id);
      if (record) return record;
      const cached = findCachedEntry<TEntry>(queryClient, cacheKey, id);
      return cached ? recordOf(cached) : undefined;
    },
    [records, queryClient, cacheKey, recordOf]
  );

  const { hasNextPage = false, isFetchingNextPage, fetchNextPage } = pages;
  const onEndReached = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const isLoadingFirstPage =
    isListing && (pages.isPending || (pages.isPlaceholderData && pages.isFetching));

  return {
    options,
    onSearch,
    onOpen,
    onEndReached,
    isLoadingMore: isFetchingNextPage,
    hasNextPage,
    isLoadingFirstPage,
    isFetching: pages.isFetching,
    isError: pages.isError,
    isValueResolving,
    findOption,
  };
}
